import React, { forwardRef, useEffect, useImperativeHandle, useRef, useState } from 'react';
import { ENTITY_LABELS, ENTITY_ICONS } from '../../services/entityMentionProviders';
import './EntityMentionList.css';

/* The "@" entity picker. Same keyboard contract as SlashCommandList and the
   legacy MentionList (up/down/enter) so every suggestion popup behaves alike. */
const EntityMentionList = forwardRef((props, ref) => {
	const [selectedIndex, setSelectedIndex] = useState(0);
	const listRef = useRef(null);

	const selectItem = (index) => {
		const item = props.items[index];
		if (item) {
			props.command({ id: item.id, label: item.label, entityType: item.type });
		}
	};

	useEffect(() => setSelectedIndex(0), [props.items]);

	/* Keep the keyboard selection inside the scroll window — see the matching
	   note in SlashCommandList. */
	useEffect(() => {
		listRef.current?.children[selectedIndex]?.scrollIntoView({ block: 'nearest' });
	}, [selectedIndex]);

	useImperativeHandle(ref, () => ({
		onKeyDown: ({ event }) => {
			if (event.key === 'ArrowUp') {
				setSelectedIndex((i) => (i + props.items.length - 1) % props.items.length);
				return true;
			}
			if (event.key === 'ArrowDown') {
				setSelectedIndex((i) => (i + 1) % props.items.length);
				return true;
			}
			if (event.key === 'Enter') {
				selectItem(selectedIndex);
				return true;
			}
			return false;
		},
	}));

	if (props.items.length === 0) {
		return (
			<div className='entity-list'>
				<div className='entity-empty'>Nothing matches</div>
			</div>
		);
	}

	return (
		<div className='entity-list' ref={listRef}>
			{props.items.map((item, index) => (
				<button
					type='button'
					key={`${item.type}-${item.id}`}
					className={`entity-item ${index === selectedIndex ? 'is-selected' : ''}`}
					onMouseEnter={() => setSelectedIndex(index)}
					onClick={() => selectItem(index)}>
					<span className={`entity-item__icon entity-item__icon--${item.type}`} aria-hidden='true'>
						<i className={`fa-solid fa-fw ${ENTITY_ICONS[item.type] || 'fa-at'}`}></i>
					</span>
					<span className='entity-item__text'>
						<span className='entity-item__label'>{item.label}</span>
						{item.sublabel && <span className='entity-item__sub'>{item.sublabel}</span>}
					</span>
					<span className='entity-item__type'>{ENTITY_LABELS[item.type] || item.type}</span>
				</button>
			))}
		</div>
	);
});

EntityMentionList.displayName = 'EntityMentionList';

export default EntityMentionList;
